import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { Download, CheckCircle, Clock, AlertCircle, TicketX } from "lucide-react";
import { ScanAPI } from "../../services/scan.api";
import PublicSplashScreen from "../../components/PublicSplashScreen";
import ErrorState from "../../components/ErrorState";
import "../../components/booking/TicketCard.scss";

export default function PublicTicketPage() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await ScanAPI.getPublicTicket(code);
        setTicket(res.data);
      } catch (err) {
        setError(err.response?.data?.message || "Tiket tidak ditemukan");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [code]);

  useEffect(() => {
    if (!ticket?.event?.headerImage?.url) {
      setHeroError(true);
      return;
    }
    setHeroLoaded(false);
    setHeroError(false);
    const img = new Image();
    img.src = ticket.event.headerImage.url;
    img.onload = () => setHeroLoaded(true);
    img.onerror = () => setHeroError(true);
  }, [ticket?.event?.headerImage?.url]);

  async function downloadTicket() {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      if (ticket?.event?.headerImage?.url && !heroError && !heroLoaded) {
        await new Promise((resolve) => {
          const img = new Image();
          img.src = ticket.event.headerImage.url;
          if (img.complete) resolve();
          else img.onload = img.onerror = resolve;
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 500));

      const heroSrc = ticket?.event?.headerImage?.url || "";
      const filter = (node) => {
        if (node.tagName === "IMG") {
          const src = node.src || "";
          if (
            src &&
            src !== heroSrc &&
            !src.startsWith(window.location.origin) &&
            !src.startsWith("data:")
          ) {
            return false;
          }
        }
        if (
          node.tagName === "LINK" &&
          node.rel === "stylesheet"
        ) {
          const href = node.href || "";
          if (href.startsWith("http") && !href.includes(window.location.hostname)) {
            return false;
          }
        }
        return true;
      };

      let dataUrl;
      try {
        dataUrl = await toPng(cardRef.current, {
          pixelRatio: 3,
          backgroundColor: "#0B1220",
          cacheBurst: true,
          filter,
        });
      } catch (pngErr) {
        dataUrl = await toPng(cardRef.current, {
          pixelRatio: 2,
          backgroundColor: "#0B1220",
          cacheBurst: true,
          filter: (node) => {
            if (node.tagName === "IMG") return false;
            if (node.tagName === "LINK" && node.rel === "stylesheet") return false;
            return true;
          },
        });
      }

      const link = document.createElement("a");
      link.download = `ticket-${ticket.code}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
      alert("Gagal download tiket. Silakan coba lagi.");
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <PublicSplashScreen />;

  if (error) {
    return (
      <ErrorState
        icon={TicketX}
        title="Tiket Tidak Ditemukan"
        message={error}
        action={{ label: "Kembali ke Beranda", href: "/" }}
      />
    );
  }

  const isUsed = ticket?.status === "used";

  return (
    <div className="public-ticket-page">
      <div className="public-ticket-card" ref={cardRef}>
        <div className="holo-layer" />
        <div className="light-sweep" />

        {/* HERO */}
        <div className="ticket-hero">
          {ticket?.event?.headerImage?.url && !heroError ? (
            <img
              src={ticket.event.headerImage.url}
              alt="Event Banner"
              style={{ opacity: heroLoaded ? 1 : 0, transition: "opacity .3s" }}
            />
          ) : (
            <div className="hero-fallback" />
          )}
          <div className="hero-gradient" />
          <div className="hero-content">
            <span className="brand">RTC GKY</span>
            <h1>{ticket?.event?.title}</h1>
          </div>
        </div>

        {/* BODY */}
        <div className="ticket-body">
          {/* Status badge */}
          <div className="ticket-status-row">
            {isUsed ? (
              <span className="status-badge used">
                <CheckCircle size={12} /> Sudah digunakan
              </span>
            ) : (
              <span className="status-badge active">
                <Clock size={12} /> Aktif
              </span>
            )}
          </div>

          {/* QR */}
          <div className="qr-zone">
            <div className="qr-pedestal">
              {ticket?.qr?.imageUrl ? (
                <img src={ticket.qr.imageUrl} alt="QR Ticket" />
              ) : (
                <div className="qr-placeholder">Memuat...</div>
              )}
            </div>
            <span className="scan-hint">Scan untuk akses masuk</span>
          </div>

          <div className="divider" />

          <div className="info-grid">
            <Info label="Nama" value={ticket?.customer?.name} variant="name" />
            <Info label="Kode Tiket" value={ticket?.code} mono />
            <Info label="Tipe Tiket" value={ticket?.ticket?.tierName} />
            <Info
              label="Event"
              value={ticket?.event?.title}
            />
          </div>

          <div className="ticket-watermark">OFFICIAL ACCESS PASS</div>

          <div className="ticket-footer">
            Berlaku untuk 1x scan • Tidak dapat dipindahtangankan
          </div>
        </div>
      </div>

      {/* Download button */}
      <button
        className="download-ultra"
        onClick={downloadTicket}
        disabled={downloading || isUsed}
      >
        <Download size={18} />
        {downloading ? "Mendownload..." : isUsed ? "Tiket Sudah Digunakan" : "Download Tiket"}
      </button>

      <p className="share-hint">
        Bagikan tiket ini ke teman kamu
      </p>
    </div>
  );
}

function Info({ label, value, mono, variant }) {
  return (
    <div className={`info-item ${variant || ""}`}>
      <span className="label">{label}</span>
      <strong className={`value ${mono ? "mono" : ""}`}>{value || "-"}</strong>
    </div>
  );
}